import { CreditCard, Banknote, CircleDot } from "lucide-react";

export const PaymentSection = ({
  isInd, isCorp, priceInfo, corpBillingCycle, corpAnnualDiscount,
  isFoundingSignup, taxRate, taxLabel, currentTier,
  paymentMethod, setPaymentMethod, etransferEmail, piConfig
}) => (
  <div className="space-y-4">
    {/* Price Summary */}
    <div className={`text-white rounded-xl p-5 ${isInd ? "bg-[#4C1D95]" : isCorp ? "bg-[#1E3A5F]" : "bg-[#1A2F23]"}`}>
      <div className="flex justify-between items-center mb-2">
        <span className="text-white/70">Your plan</span>
        <span className="font-semibold">{currentTier?.display_name}{(isCorp || isInd) ? ` (${corpBillingCycle})` : ""}</span>
      </div>
      {(isCorp || isInd) && corpBillingCycle === "annual" && priceInfo.annualSaved > 0 && (
        <div className="flex justify-between items-center text-sm">
          <span className="text-[#14B8A6]">Annual discount ({corpAnnualDiscount}%)</span>
          <span className="text-[#14B8A6]">-${priceInfo.annualSaved}</span>
        </div>
      )}
      {isFoundingSignup && priceInfo.foundingDiscount > 0 && (
        <div className="flex justify-between items-center text-sm">
          <span className="text-[#14B8A6]">Founding discount (20%)</span>
          <span className="text-[#14B8A6]">-${priceInfo.foundingDiscount}</span>
        </div>
      )}
      <div className="flex justify-between items-center text-sm mt-1">
        <span className="text-white/70">Subtotal</span>
        <span className="text-white/90">${priceInfo.subtotal?.toLocaleString()}{priceInfo.period === "monthly" ? "/mo" : "/yr"}</span>
      </div>
      <div className="flex justify-between items-center text-sm">
        <span className="text-white/70">{taxLabel} ({(taxRate * 100).toFixed(0)}%)</span>
        <span className="text-white/90">${priceInfo.tax?.toFixed(2)}</span>
      </div>
      <div className="border-t border-white/20 mt-3 pt-3 flex justify-between items-center">
        <span className="font-bold text-lg">Total</span>
        <span className="font-bold text-2xl">{priceInfo.label}</span>
      </div>
    </div>

    {/* Payment Method */}
    <h3 className="font-heading text-lg font-semibold text-[#1A1915]">Payment Method</h3>
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      <button type="button" disabled
        className="flex items-center gap-2 p-4 rounded-xl border-2 border-[#D1CFC5] opacity-50 cursor-not-allowed relative"
        data-testid="pay-stripe">
        <CreditCard className="w-5 h-5 text-[#5C5A52]" />
        <div className="text-left">
          <p className="font-semibold text-[#5C5A52] text-sm">Pay with Card</p>
          <p className="text-xs text-[#5C5A52]">Coming Soon</p>
        </div>
      </button>
      <button type="button" onClick={() => setPaymentMethod("etransfer")}
        className={`flex items-center gap-2 p-4 rounded-xl border-2 transition-all ${paymentMethod === "etransfer" ? "border-[#2C4A3B] bg-[#2C4A3B]/5" : "border-[#D1CFC5]"}`}
        data-testid="pay-etransfer">
        <Banknote className="w-5 h-5 text-[#2C4A3B]" />
        <div className="text-left">
          <p className="font-semibold text-[#1A1915] text-sm">E-Transfer</p>
          <p className="text-xs text-[#5C5A52]">Send to {etransferEmail}</p>
        </div>
      </button>
      <button type="button"
        onClick={() => setPaymentMethod("pi")}
        className={`flex items-center gap-2 p-4 rounded-xl border-2 transition-all ${
          paymentMethod === "pi" ? "border-[#7B4FC4] bg-[#7B4FC4]/5" : "border-[#D1CFC5]"
        }`}
        data-testid="pay-pi">
        <CircleDot className="w-5 h-5 text-[#7B4FC4]" />
        <div className="text-left">
          <p className="font-semibold text-[#1A1915] text-sm">Pay with Pi</p>
          <p className="text-xs text-[#5C5A52]">Via Pi Wallet</p>
        </div>
      </button>
    </div>

    {paymentMethod === "etransfer" && (
      <div className="mt-4 bg-white border-2 border-[#D1CFC5] rounded-xl p-5" data-testid="etransfer-instructions">
        <h4 className="font-heading text-base font-semibold text-[#1A1915] mb-3">E-Transfer Instructions</h4>
        <ol className="space-y-2 text-sm text-[#5C5A52]">
          <li className="flex gap-2"><span className="font-bold text-[#1A1915]">1.</span> Submit your listing below — we'll review it right away</li>
          <li className="flex gap-2"><span className="font-bold text-[#1A1915]">2.</span> Send <strong className="text-[#1A1915]">{priceInfo.label}</strong> to <strong className="text-[#1A1915]">{etransferEmail}</strong> via Interac E-Transfer</li>
          <li className="flex gap-2"><span className="font-bold text-[#1A1915]">3.</span> Include your business name in the E-Transfer message</li>
        </ol>
      </div>
    )}

    {paymentMethod === "pi" && (
      <div className="mt-4 bg-[#7B4FC4]/5 border-2 border-[#7B4FC4]/30 rounded-xl p-5" data-testid="pi-payment-instructions">
        <h4 className="font-heading text-base font-semibold text-[#1A1915] mb-2">Pay with Pi</h4>
        <p className="text-sm text-[#5C5A52]">Click &quot;Submit Listing&quot; below to open Pi Wallet. You&apos;ll confirm the payment in the Pi Browser.</p>
      </div>
    )}
  </div>
);
